import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  AlertTriangle,
  CheckCircle2,
  Code2,
  FileCode2,
  FileText,
  Film,
  Folder,
  FolderGit2,
  MousePointer,
  Search,
  Settings,
  Video,
  XCircle,
  Zap,
} from "lucide-react";
import { SubtitleItem } from "./components/DynamicSubtitle";

// 11 Timed Subtitles matching Zura's actual voice recording
const SUBTITLES: SubtitleItem[] = [
  {
    from: 0,
    to: 180,
    text: "Video estetik dengan animasi se-smooth ini sama sekali gak gue sentuh pake CapCut ataupun Premiere.",
    highlight: "gak gue sentuh pake CapCut",
  },
  {
    from: 180,
    to: 310,
    text: "Tapi 100% dibikin otomatis pake AI dan barisan kode.",
    highlight: "100% dibikin otomatis pake AI",
  },
  {
    from: 310,
    to: 455,
    text: "Selama ini kita taunya bikin video pendek yang rapi itu harus pusing geser timeline,",
    highlight: "pusing geser timeline",
  },
  {
    from: 455,
    to: 605,
    text: "pasang keyframe manual satu per satu, dan nunggu rendering berjam-jam sampai laptop panas.",
    highlight: "nunggu rendering berjam-jam",
  },
  {
    from: 605,
    to: 665,
    text: "Tapi sekarang dunianya udah beda.",
    highlight: "dunianya udah beda",
  },
  {
    from: 665,
    to: 850,
    text: "Gue cuma perlu lempar ide 1 kalimat, dan AI langsung nulis kodingan React buat nentuin tipografi,",
    highlight: "AI langsung nulis kodingan React",
  },
  {
    from: 850,
    to: 1035,
    text: "ngatur transisi 60 FPS sampai ngerender video finalnya sendiri langsung dari terminal server.",
    highlight: "transisi 60 FPS & render di server",
  },
  {
    from: 1035,
    to: 1140,
    text: "Nol sentuhan mouse dan nol drama timeline macet.",
    highlight: "Nol sentuhan mouse",
  },
  {
    from: 1140,
    to: 1355,
    text: "Ini bukan sekadar jalan pintas, tapi era baru di mana AI beneran kerja nyata buat ngegantiin proses teknis yang ribet.",
    highlight: "AI beneran kerja nyata",
  },
  {
    from: 1355,
    to: 1485,
    text: "Semua video di akun ini bakal jadi bukti eksperimen langsung di depan mata lo.",
    highlight: "bukti eksperimen langsung",
  },
  {
    from: 1485,
    to: 1710,
    text: "Kalau lo mau tau sejauh mana AI masa depan bisa ngerubah cara kita bikin konten, follow Zura Tech Lab dan pantau eksperimen berikutnya.",
    highlight: "follow Zura Tech Lab",
  },
];

// FONT DECLARATIONS VIA STATICFILE (Raw Heavy Poster Type)
const fontFaces = `
@font-face {
  font-family: 'TekoDisplay';
  src: url('${staticFile("fonts/Teko-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
@font-face {
  font-family: 'BebasDisplay';
  src: url('${staticFile("fonts/BebasNeue-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'AntonDisplay';
  src: url('${staticFile("fonts/Anton-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'RajdhaniDisplay';
  src: url('${staticFile("fonts/Rajdhani-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
@font-face {
  font-family: 'ChakraDisplay';
  src: url('${staticFile("fonts/ChakraPetch-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
`;

export const ZuraAIDebutRemotion: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Snappy brutalist springs
  const sp = (delay: number, damping = 14, stiffness = 180) =>
    spring({
      frame: Math.max(0, frame - delay),
      fps,
      config: { damping, stiffness, mass: 0.8 },
    });

  // Authentic Neo-Brutalism Color Palette (Zero AI-Slop Glows)
  const canvasBg = "#F4F0EA"; // Warm tactile raw paper / eggshell
  const inkBlack = "#000000"; // Solid dense black
  const pureWhite = "#FFFFFF"; // Clean stark white
  const canaryYellow = "#FFE500"; // Industrial warning canary yellow
  const hazardOrange = "#FF5500"; // Punchy safety hazard orange
  const cobaltBlue = "#1B44FF"; // Electric royal cobalt blue
  const coralRed = "#FF4365"; // Punchy warning coral
  const mintGreen = "#00D26A"; // Solid energetic mint green
  const slateCard = "#181D28"; // Deep slate for code containers

  // Marquee tape animation
  const tapeSlide = interpolate(frame, [0, 300], [0, -450]);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: canvasBg,
        color: inkBlack,
        fontFamily: "'Space Grotesk', 'Inter', -apple-system, sans-serif",
        overflow: "hidden",
      }}
    >
      <style>{fontFaces}</style>

      {/* Audio Layer - Zura's Voiceover */}
      <Audio src={staticFile("zura_debut/zura_vo.wav")} />

      {/* Architectural Corner Crosshairs (+) in Pure Black */}
      <div style={{ position: "absolute", top: 36, left: 40, fontFamily: "'AntonDisplay', sans-serif", fontSize: 32, fontWeight: 900, color: inkBlack }}>+</div>
      <div style={{ position: "absolute", top: 36, right: 40, fontFamily: "'AntonDisplay', sans-serif", fontSize: 32, fontWeight: 900, color: inkBlack }}>+</div>
      <div style={{ position: "absolute", bottom: 40, left: 40, fontFamily: "'AntonDisplay', sans-serif", fontSize: 32, fontWeight: 900, color: inkBlack }}>+</div>
      <div style={{ position: "absolute", bottom: 40, right: 40, fontFamily: "'AntonDisplay', sans-serif", fontSize: 32, fontWeight: 900, color: inkBlack }}>+</div>

      {/* Tilted Industrial Marquee Tape (Top Accent) */}
      <div
        style={{
          position: "absolute",
          top: 100,
          left: -80,
          right: -80,
          height: 52,
          backgroundColor: canaryYellow,
          borderTop: `4px solid ${inkBlack}`,
          borderBottom: `4px solid ${inkBlack}`,
          display: "flex",
          alignItems: "center",
          transform: "rotate(-2deg)",
          overflow: "hidden",
          boxShadow: `0 8px 0px ${inkBlack}`,
          zIndex: 40,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 40,
            whiteSpace: "nowrap",
            fontSize: 24,
            fontFamily: "'TekoDisplay', sans-serif",
            fontWeight: 700,
            letterSpacing: "0.12em",
            color: inkBlack,
            transform: `translateX(${tapeSlide}px)`,
          }}
        >
          <span>/// ZURA TECH LAB /// KODE JADI VIDEO 60 FPS /// TANPA CAPCUT /// SISTEM REMOTION REACT /// 100% OTOMATIS ///</span>
          <span>/// ZURA TECH LAB /// KODE JADI VIDEO 60 FPS /// TANPA CAPCUT /// SISTEM REMOTION REACT /// 100% OTOMATIS ///</span>
          <span>/// ZURA TECH LAB /// KODE JADI VIDEO 60 FPS /// TANPA CAPCUT /// SISTEM REMOTION REACT /// 100% OTOMATIS ///</span>
        </div>
      </div>

      {/* Top Status Bar (Safe Area: Y 220px+) */}
      <div
        style={{
          position: "absolute",
          top: 220,
          left: 80,
          right: 80,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div
          style={{
            backgroundColor: inkBlack,
            color: pureWhite,
            padding: "8px 20px",
            fontSize: 18,
            fontWeight: 800,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "0.06em",
            border: `3px solid ${inkBlack}`,
            boxShadow: `5px 5px 0px ${hazardOrange}`,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: canaryYellow }} />
          <span>ZURA TECH LAB</span>
        </div>

        <div
          style={{
            backgroundColor: canaryYellow,
            color: inkBlack,
            padding: "8px 18px",
            fontSize: 18,
            fontWeight: 800,
            fontFamily: "'JetBrains Mono', monospace",
            border: `3px solid ${inkBlack}`,
            boxShadow: `5px 5px 0px ${inkBlack}`,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Zap size={20} strokeWidth={3} color={inkBlack} />
          <span>60 FPS STABIL</span>
        </div>
      </div>

      {/* =========================================================================
          SCENE 1: THE BRUTALIST HOOK (Frames 0 - 180 / 0.0s - 6.0s)
          VO: "Video estetik dengan animasi se-smooth ini sama sekali gak gue sentuh pake CapCut ataupun Premiere."
          ========================================================================= */}
      {frame >= 0 && frame < 180 && (
        <div
          style={{
            position: "absolute",
            top: 320,
            left: 80,
            right: 80,
            display: "flex",
            flexDirection: "column",
            gap: 20,
            zIndex: 30,
            transform: `translateY(${interpolate(sp(0), [0, 1], [40, 0])}px)`,
          }}
        >
          {/* Main White Hero Box with Hard Offset Shadow */}
          <div
            style={{
              backgroundColor: pureWhite,
              border: `4px solid ${inkBlack}`,
              boxShadow: `10px 10px 0px ${inkBlack}`,
              padding: "36px 32px",
              display: "flex",
              flexDirection: "column",
              gap: 16,
              position: "relative",
            }}
          >
            {/* Tilted Stamp Tag */}
            <div
              style={{
                position: "absolute",
                top: -20,
                right: 24,
                backgroundColor: hazardOrange,
                color: pureWhite,
                padding: "6px 18px",
                fontFamily: "'TekoDisplay', sans-serif",
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: "0.08em",
                border: `3px solid ${inkBlack}`,
                boxShadow: `4px 4px 0px ${inkBlack}`,
                transform: "rotate(3deg)",
              }}
            >
              TANPA EDIT TIMELINE
            </div>

            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 18,
                fontWeight: 800,
                letterSpacing: "0.08em",
                color: cobaltBlue,
              }}
            >
              // PEMBUKTIAN VISUAL
            </div>

            <h1
              style={{
                fontFamily: "'TekoDisplay', sans-serif",
                fontSize: 98,
                fontWeight: 700,
                lineHeight: 0.92,
                letterSpacing: "0.02em",
                margin: 0,
                color: inkBlack,
                textTransform: "uppercase",
              }}
            >
              VIDEO ESTETIK 60 FPS
            </h1>

            {/* High-Impact Yellow Highlight Banner */}
            <div
              style={{
                backgroundColor: canaryYellow,
                border: `3px solid ${inkBlack}`,
                boxShadow: `5px 5px 0px ${inkBlack}`,
                padding: "12px 18px",
                display: "inline-block",
                transform: "rotate(-1deg)",
              }}
            >
              <h2
                style={{
                  fontFamily: "'TekoDisplay', sans-serif",
                  fontSize: 68,
                  fontWeight: 700,
                  lineHeight: 0.9,
                  letterSpacing: "0.02em",
                  margin: 0,
                  color: inkBlack,
                  textTransform: "uppercase",
                }}
              >
                GAK DIEDIT DI CAPCUT
              </h2>
            </div>

            {/* Micro Feature Stickers */}
            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
              <div
                style={{
                  flex: 1,
                  backgroundColor: inkBlack,
                  color: pureWhite,
                  padding: "12px 16px",
                  border: `3px solid ${inkBlack}`,
                  boxShadow: `4px 4px 0px ${canaryYellow}`,
                  fontSize: 18,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <CheckCircle2 size={20} color={mintGreen} />
                <span>NOL KEYFRAME</span>
              </div>
              <div
                style={{
                  flex: 1,
                  backgroundColor: pureWhite,
                  color: inkBlack,
                  padding: "12px 16px",
                  border: `3px solid ${inkBlack}`,
                  boxShadow: `4px 4px 0px ${inkBlack}`,
                  fontSize: 18,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Code2 size={20} color={cobaltBlue} />
                <span>100% KODE MURNI</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          SCENE 2: BANNED SOFTWARE STAMP (Frames 180 - 455 / 6.0s - 15.1s)
          VO: "Selama ini kita taunya bikin video pendek yang rapi itu harus pusing geser timeline..."
          ========================================================================= */}
      {frame >= 180 && frame < 455 && (
        <div
          style={{
            position: "absolute",
            top: 320,
            left: 80,
            right: 80,
            display: "flex",
            flexDirection: "column",
            gap: 20,
            zIndex: 30,
            transform: `translateY(${interpolate(sp(180), [0, 1], [40, 0])}px)`,
          }}
        >
          {/* Header Tag */}
          <div
            style={{
              backgroundColor: hazardOrange,
              color: pureWhite,
              border: `3px solid ${inkBlack}`,
              boxShadow: `4px 4px 0px ${inkBlack}`,
              padding: "8px 18px",
              fontFamily: "'TekoDisplay', sans-serif",
              fontSize: 26,
              fontWeight: 700,
              letterSpacing: "0.06em",
              alignSelf: "flex-start",
            }}
          >
            SOFTWARE LAMA DITINGGALKAN
          </div>

          {/* Symmetrical Neo-Brutalist Banned Cards */}
          <div style={{ display: "flex", gap: 16 }}>
            {/* CapCut Card */}
            <div
              style={{
                flex: 1,
                backgroundColor: pureWhite,
                border: `4px solid ${inkBlack}`,
                boxShadow: `8px 8px 0px ${inkBlack}`,
                padding: "28px 16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                position: "relative",
              }}
            >
              <Film size={44} color={inkBlack} />
              <div
                style={{
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 52,
                  letterSpacing: "0.04em",
                  color: inkBlack,
                  position: "relative",
                  display: "inline-block",
                  lineHeight: 1,
                }}
              >
                CAPCUT
                {/* Thick Hard Diagonal Red Stamp */}
                <div
                  style={{
                    position: "absolute",
                    top: "45%",
                    left: -16,
                    right: -16,
                    height: 12,
                    backgroundColor: coralRed,
                    border: `2.5px solid ${inkBlack}`,
                    transform: "rotate(-14deg)",
                  }}
                />
              </div>
              <div
                style={{
                  backgroundColor: coralRed,
                  color: pureWhite,
                  border: `2px solid ${inkBlack}`,
                  padding: "4px 10px",
                  fontSize: 16,
                  fontWeight: 900,
                  letterSpacing: "0.08em",
                }}
              >
                DITOLAK
              </div>
            </div>

            {/* Premiere Card */}
            <div
              style={{
                flex: 1,
                backgroundColor: pureWhite,
                border: `4px solid ${inkBlack}`,
                boxShadow: `8px 8px 0px ${inkBlack}`,
                padding: "28px 16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                position: "relative",
              }}
            >
              <Video size={44} color={inkBlack} />
              <div
                style={{
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 52,
                  letterSpacing: "0.04em",
                  color: inkBlack,
                  position: "relative",
                  display: "inline-block",
                  lineHeight: 1,
                }}
              >
                PREMIERE
                <div
                  style={{
                    position: "absolute",
                    top: "45%",
                    left: -16,
                    right: -16,
                    height: 12,
                    backgroundColor: coralRed,
                    border: `2.5px solid ${inkBlack}`,
                    transform: "rotate(-14deg)",
                  }}
                />
              </div>
              <div
                style={{
                  backgroundColor: coralRed,
                  color: pureWhite,
                  border: `2px solid ${inkBlack}`,
                  padding: "4px 10px",
                  fontSize: 16,
                  fontWeight: 900,
                  letterSpacing: "0.08em",
                }}
              >
                DITOLAK
              </div>
            </div>
          </div>

          {/* Brutalist Timeline Simulation */}
          <div
            style={{
              backgroundColor: pureWhite,
              border: `4px solid ${inkBlack}`,
              boxShadow: `8px 8px 0px ${inkBlack}`,
              padding: "24px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `2px solid ${inkBlack}`, paddingBottom: 8 }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 900 }}>TIMELINE KREATOR MANUAL</span>
              <span style={{ backgroundColor: canaryYellow, border: `2px solid ${inkBlack}`, padding: "2px 8px", fontSize: 14, fontWeight: 800 }}>PUSING TRACK</span>
            </div>

            {[
              { label: "Jalur 4: Teks Hook", w: "92%", bg: coralRed, fg: pureWhite },
              { label: "Jalur 3: Stiker Animasi", w: "76%", bg: canaryYellow, fg: inkBlack },
              { label: "Jalur 2: Video Cuplikan", w: "88%", bg: cobaltBlue, fg: pureWhite },
              { label: "Jalur 1: Rekaman Suara", w: "100%", bg: mintGreen, fg: inkBlack },
            ].map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 180, fontSize: 16, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>{t.label}</span>
                <div style={{ flex: 1, height: 26, backgroundColor: canvasBg, border: `2px solid ${inkBlack}`, position: "relative" }}>
                  <div style={{ width: t.w, height: "100%", backgroundColor: t.bg, borderRight: `2px solid ${inkBlack}` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* =========================================================================
          SCENE 3: BRUTALIST SYSTEM CRASH & KEYFRAME OVERLOAD (Frames 455 - 665)
          VO: "pasang keyframe manual satu per satu, dan nunggu rendering berjam-jam sampai laptop panas. Tapi sekarang dunianya udah beda."
          ========================================================================= */}
      {frame >= 455 && frame < 665 && (
        <div
          style={{
            position: "absolute",
            top: 320,
            left: 80,
            right: 80,
            display: "flex",
            flexDirection: "column",
            gap: 20,
            zIndex: 30,
            transform: `translateY(${interpolate(sp(455), [0, 1], [40, 0])}px)`,
          }}
        >
          {/* Neo-Brutalist System Crash Window */}
          <div
            style={{
              backgroundColor: pureWhite,
              border: `4px solid ${inkBlack}`,
              boxShadow: `10px 10px 0px ${inkBlack}`,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Title Bar */}
            <div
              style={{
                backgroundColor: inkBlack,
                color: pureWhite,
                padding: "12px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <AlertTriangle size={20} color={canaryYellow} />
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 16,
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                  }}
                >
                  SISTEM_ERROR: MEMORI_PENUH
                </span>
              </div>
              <div
                style={{
                  backgroundColor: coralRed,
                  width: 20,
                  height: 20,
                  border: `2px solid ${pureWhite}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 900,
                  color: pureWhite,
                }}
              >
                X
              </div>
            </div>

            {/* Dialog Content Body */}
            <div
              style={{
                padding: "32px 28px",
                display: "flex",
                flexDirection: "column",
                gap: 18,
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: 80,
                  height: 80,
                  backgroundColor: coralRed,
                  border: `3px solid ${inkBlack}`,
                  boxShadow: `4px 4px 0px ${inkBlack}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <XCircle size={48} color={pureWhite} />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div
                  style={{
                    fontFamily: "'TekoDisplay', sans-serif",
                    fontSize: 90,
                    fontWeight: 700,
                    lineHeight: 0.88,
                    color: coralRed,
                    letterSpacing: "0.02em",
                  }}
                >
                  TIMELINE JEBOL
                </div>
                <div
                  style={{
                    fontFamily: "'ChakraDisplay', sans-serif",
                    fontSize: 32,
                    fontWeight: 800,
                    color: inkBlack,
                  }}
                >
                  840 TITIK KEYFRAME RUSAK
                </div>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: 20,
                    color: "#555",
                    maxWidth: 620,
                    lineHeight: 1.4,
                    marginTop: 4,
                  }}
                >
                  GPU kepanasan. Proses render macet total setelah nunggu 3 jam.
                </div>
              </div>

              {/* Frozen Progress Bar */}
              <div
                style={{
                  width: "100%",
                  height: 32,
                  backgroundColor: canvasBg,
                  border: `3px solid ${inkBlack}`,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: "14%",
                    height: "100%",
                    backgroundColor: coralRed,
                    borderRight: `3px solid ${inkBlack}`,
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 16,
                    fontWeight: 900,
                    color: inkBlack,
                  }}
                >
                  MACET DI 14% (0 FPS)
                </span>
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: 14, width: "100%", marginTop: 4 }}>
                <div
                  style={{
                    flex: 1,
                    backgroundColor: coralRed,
                    color: pureWhite,
                    border: `3px solid ${inkBlack}`,
                    boxShadow: `4px 4px 0px ${inkBlack}`,
                    padding: "14px",
                    fontFamily: "'ChakraDisplay', sans-serif",
                    fontSize: 24,
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                  }}
                >
                  TUTUP PAKSA
                </div>
                <div
                  style={{
                    flex: 1,
                    backgroundColor: canaryYellow,
                    color: inkBlack,
                    border: `3px solid ${inkBlack}`,
                    boxShadow: `4px 4px 0px ${inkBlack}`,
                    padding: "14px",
                    fontFamily: "'ChakraDisplay', sans-serif",
                    fontSize: 24,
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                  }}
                >
                  TUNGGU 3 JAM
                </div>
              </div>
            </div>
          </div>

          {/* Glitch Punch: TAPI SEKARANG DUNIANYA UDAH BEDA */}
          {frame >= 605 && (
            <div
              style={{
                backgroundColor: canaryYellow,
                border: `4px solid ${inkBlack}`,
                boxShadow: `8px 8px 0px ${inkBlack}`,
                padding: "18px 24px",
                textAlign: "center",
                transform: "rotate(-1.5deg)",
              }}
            >
              <span
                style={{
                  fontFamily: "'TekoDisplay', sans-serif",
                  fontSize: 70,
                  fontWeight: 700,
                  color: inkBlack,
                  letterSpacing: "0.04em",
                  lineHeight: 1,
                }}
              >
                TAPI SEKARANG DUNIANYA UDAH BEDA
              </span>
            </div>
          )}
        </div>
      )}

      {/* =========================================================================
          SCENE 4: REAL REACT IDE STUDIO (Frames 665 - 850 / 22.2s - 28.3s)
          VO: "Gue cuma perlu lempar ide 1 kalimat, dan AI langsung nulis kodingan React buat nentuin tipografi,"
          ========================================================================= */}
      {frame >= 665 && frame < 850 && (
        <div
          style={{
            position: "absolute",
            top: 320,
            left: 80,
            right: 80,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            zIndex: 30,
            transform: `translateY(${interpolate(sp(665), [0, 1], [40, 0])}px)`,
          }}
        >
          {/* Header Pill */}
          <div
            style={{
              backgroundColor: cobaltBlue,
              color: pureWhite,
              border: `3px solid ${inkBlack}`,
              boxShadow: `4px 4px 0px ${inkBlack}`,
              padding: "8px 18px",
              fontFamily: "'TekoDisplay', sans-serif",
              fontSize: 26,
              fontWeight: 700,
              alignSelf: "flex-start",
            }}
          >
            AI NULIS KODINGAN REACT OTONOM
          </div>

          {/* Real Neo-Brutalist VS Code Container */}
          <div
            style={{
              backgroundColor: pureWhite,
              border: `4px solid ${inkBlack}`,
              boxShadow: `10px 10px 0px ${inkBlack}`,
              borderRadius: 0,
              display: "flex",
              height: 480,
              overflow: "hidden",
            }}
          >
            {/* Left Activity Bar */}
            <div
              style={{
                width: 58,
                backgroundColor: canvasBg,
                borderRight: `3px solid ${inkBlack}`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "20px 0",
                gap: 22,
              }}
            >
              <FileCode2 size={24} color={inkBlack} />
              <Search size={22} color={inkBlack} />
              <FolderGit2 size={22} color={inkBlack} />
              <Settings size={22} color={inkBlack} style={{ marginTop: "auto" }} />
            </div>

            {/* Middle Explorer Tree */}
            <div
              style={{
                width: 200,
                backgroundColor: pureWhite,
                borderRight: `3px solid ${inkBlack}`,
                padding: "18px 14px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 15,
                fontWeight: 700,
              }}
            >
              <div style={{ backgroundColor: canaryYellow, border: `2px solid ${inkBlack}`, padding: "4px 8px", fontSize: 13, fontWeight: 900 }}>BERKAS PROYEK</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Folder size={16} color={inkBlack} />
                <span>src/</span>
              </div>
              <div style={{ paddingLeft: 16, color: cobaltBlue, display: "flex", alignItems: "center", gap: 6, fontWeight: 900 }}>
                <FileCode2 size={16} color={cobaltBlue} />
                <span>ZuraVideo.tsx</span>
              </div>
              <div style={{ paddingLeft: 16, color: "#666", display: "flex", alignItems: "center", gap: 6 }}>
                <FileText size={16} color="#666" />
                <span>tokens.ts</span>
              </div>
              <div style={{ paddingLeft: 16, color: "#666", display: "flex", alignItems: "center", gap: 6 }}>
                <FileText size={16} color="#666" />
                <span>theme.css</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <FileText size={16} color={inkBlack} />
                <span>package.json</span>
              </div>
            </div>

            {/* Right Main Editor Body */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                backgroundColor: slateCard,
                color: pureWhite,
              }}
            >
              {/* Tab Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: inkBlack,
                  borderBottom: `3px solid ${inkBlack}`,
                  padding: "0 16px",
                  height: 42,
                }}
              >
                <div
                  style={{
                    backgroundColor: slateCard,
                    borderTop: `3px solid ${canaryYellow}`,
                    padding: "8px 16px",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 15,
                    fontWeight: 700,
                    color: pureWhite,
                  }}
                >
                  ZuraVideo.tsx
                </div>
              </div>

              {/* Code Lines with High-Contrast Syntax Colors */}
              <div
                style={{
                  padding: "20px 24px",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 22,
                  lineHeight: 1.7,
                  color: pureWhite,
                }}
              >
                <div>
                  <span style={{ color: "#888" }}>01</span> <span style={{ color: canaryYellow }}>import</span> {`{ spring, useCurrentFrame }`}{" "}
                  <span style={{ color: canaryYellow }}>from</span> <span style={{ color: mintGreen }}>"remotion"</span>;
                </div>
                <div>
                  <span style={{ color: "#888" }}>02</span> <span style={{ color: canaryYellow }}>export const</span> Video = () =&gt; {`{`}
                </div>
                <div style={{ paddingLeft: 20 }}>
                  <span style={{ color: "#888" }}>03</span> <span style={{ color: canaryYellow }}>const</span> frame = useCurrentFrame();
                </div>
                <div style={{ paddingLeft: 20 }}>
                  <span style={{ color: "#888" }}>04</span> <span style={{ color: canaryYellow }}>const</span> scale = spring({`{ frame, fps: 60 }`});
                </div>
                <div style={{ paddingLeft: 20 }}>
                  <span style={{ color: "#888" }}>05</span> <span style={{ color: canaryYellow }}>return</span> &lt;Title scale={`{scale}`}&gt;CODE-TO-VIDEO&lt;/Title&gt;;
                </div>
                <div>
                  <span style={{ color: "#888" }}>06</span> {`};`}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          SCENE 5: MONOLITHIC 0 CLICK / 0 LAG (Frames 850 - 1140 / 28.3s - 38.0s)
          VO: "ngatur transisi 60 FPS sampai ngerender video finalnya sendiri langsung dari terminal server. Nol sentuhan mouse dan nol drama timeline macet."
          ========================================================================= */}
      {frame >= 850 && frame < 1140 && (
        <div
          style={{
            position: "absolute",
            top: 320,
            left: 80,
            right: 80,
            display: "flex",
            flexDirection: "column",
            gap: 20,
            zIndex: 30,
            transform: `translateY(${interpolate(sp(850), [0, 1], [40, 0])}px)`,
          }}
        >
          {/* Side by Side Monolithic Neo-Brutalist Stat Cards */}
          <div style={{ display: "flex", gap: 16 }}>
            {/* 0 CLICK CARD in Canary Yellow */}
            <div
              style={{
                flex: 1,
                backgroundColor: canaryYellow,
                border: `4px solid ${inkBlack}`,
                boxShadow: `8px 8px 0px ${inkBlack}`,
                padding: "24px 18px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 8,
              }}
            >
              <MousePointer size={44} color={inkBlack} />
              <div
                style={{
                  fontFamily: "'TekoDisplay', sans-serif",
                  fontSize: 96,
                  fontWeight: 700,
                  lineHeight: 0.85,
                  color: inkBlack,
                }}
              >
                0 KLIK
              </div>
              <div
                style={{
                  backgroundColor: inkBlack,
                  color: pureWhite,
                  padding: "4px 12px",
                  fontSize: 16,
                  fontWeight: 900,
                  letterSpacing: "0.08em",
                }}
              >
                NOL SENTUHAN MOUSE
              </div>
            </div>

            {/* 0 LAG CARD in Mint Green */}
            <div
              style={{
                flex: 1,
                backgroundColor: mintGreen,
                border: `4px solid ${inkBlack}`,
                boxShadow: `8px 8px 0px ${inkBlack}`,
                padding: "24px 18px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 8,
              }}
            >
              <Zap size={44} color={inkBlack} />
              <div
                style={{
                  fontFamily: "'TekoDisplay', sans-serif",
                  fontSize: 96,
                  fontWeight: 700,
                  lineHeight: 0.85,
                  color: inkBlack,
                }}
              >
                0 LAG
              </div>
              <div
                style={{
                  backgroundColor: inkBlack,
                  color: pureWhite,
                  padding: "4px 12px",
                  fontSize: 16,
                  fontWeight: 900,
                  letterSpacing: "0.08em",
                }}
              >
                ANTI TIMELINE MACET
              </div>
            </div>
          </div>

          {/* Server Telemetry Box in White */}
          <div
            style={{
              backgroundColor: pureWhite,
              border: `4px solid ${inkBlack}`,
              boxShadow: `8px 8px 0px ${inkBlack}`,
              padding: "24px 22px",
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 900 }}>
                &gt; npx remotion render --concurrency=8
              </span>
              <span
                style={{
                  backgroundColor: cobaltBlue,
                  color: pureWhite,
                  border: `2px solid ${inkBlack}`,
                  padding: "4px 10px",
                  fontSize: 15,
                  fontWeight: 800,
                }}
              >
                60.0 FPS STABIL
              </span>
            </div>

            {/* Telemetry Numbers */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div style={{ backgroundColor: canvasBg, border: `2px solid ${inkBlack}`, padding: "10px", textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 800 }}>BEBAN GPU</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: hazardOrange, fontFamily: "'TekoDisplay', sans-serif" }}>89% TERPAKAI</div>
              </div>
              <div style={{ backgroundColor: canvasBg, border: `2px solid ${inkBlack}`, padding: "10px", textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 800 }}>MEMORI VRAM</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: inkBlack, fontFamily: "'TekoDisplay', sans-serif" }}>1.4 GB / 32 GB</div>
              </div>
              <div style={{ backgroundColor: canvasBg, border: `2px solid ${inkBlack}`, padding: "10px", textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 800 }}>WAKTU RENDER</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: mintGreen, fontFamily: "'TekoDisplay', sans-serif" }}>0.28 DETIK</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          SCENE 6: PRODUCTION REVOLUTION & ASSETS VAULT (Frames 1140 - 1485)
          VO: "Ini bukan sekadar jalan pintas, tapi era baru di mana AI beneran kerja nyata... Semua video di akun ini bakal jadi bukti eksperimen langsung di depan mata lo."
          ========================================================================= */}
      {frame >= 1140 && frame < 1485 && (
        <div
          style={{
            position: "absolute",
            top: 320,
            left: 80,
            right: 80,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            zIndex: 30,
            transform: `translateY(${interpolate(sp(1140), [0, 1], [40, 0])}px)`,
          }}
        >
          {/* Top Hero Card in Cobalt Blue */}
          <div
            style={{
              backgroundColor: cobaltBlue,
              color: pureWhite,
              border: `4px solid ${inkBlack}`,
              boxShadow: `10px 10px 0px ${inkBlack}`,
              padding: "28px 24px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -16,
                right: 20,
                backgroundColor: canaryYellow,
                color: inkBlack,
                border: `3px solid ${inkBlack}`,
                boxShadow: `3px 3px 0px ${inkBlack}`,
                padding: "4px 14px",
                fontFamily: "'TekoDisplay', sans-serif",
                fontSize: 22,
                fontWeight: 700,
                transform: "rotate(2deg)",
              }}
            >
              PARADIGMA BARU 2026
            </div>

            <div
              style={{
                fontFamily: "'TekoDisplay', sans-serif",
                fontSize: 84,
                fontWeight: 700,
                lineHeight: 0.92,
                color: pureWhite,
                letterSpacing: "0.02em",
                margin: 0,
              }}
            >
              AI BENERAN KERJA NYATA
            </div>

            <div
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 20,
                color: canvasBg,
                lineHeight: 1.35,
              }}
            >
              Bukan chatbot teks yang ngasih teori. Tapi sistem otonom yang ngoding dan ngerender video sampai beres.
            </div>
          </div>

          {/* 3 Metric Bento Tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div
              style={{
                backgroundColor: canaryYellow,
                border: `3.5px solid ${inkBlack}`,
                boxShadow: `6px 6px 0px ${inkBlack}`,
                padding: "16px 12px",
                textAlign: "center",
              }}
            >
              <div style={{ fontFamily: "'TekoDisplay', sans-serif", fontSize: 62, fontWeight: 700, lineHeight: 0.85 }}>
                30 DETIK
              </div>
              <div style={{ fontSize: 13, fontWeight: 900, letterSpacing: "0.08em", marginTop: 4 }}>
                RENDER KILAT
              </div>
            </div>

            <div
              style={{
                backgroundColor: mintGreen,
                border: `3.5px solid ${inkBlack}`,
                boxShadow: `6px 6px 0px ${inkBlack}`,
                padding: "16px 12px",
                textAlign: "center",
              }}
            >
              <div style={{ fontFamily: "'TekoDisplay', sans-serif", fontSize: 62, fontWeight: 700, lineHeight: 0.85 }}>
                60 FPS
              </div>
              <div style={{ fontSize: 13, fontWeight: 900, letterSpacing: "0.08em", marginTop: 4 }}>
                SMOOTH SUTRA
              </div>
            </div>

            <div
              style={{
                backgroundColor: hazardOrange,
                color: pureWhite,
                border: `3.5px solid ${inkBlack}`,
                boxShadow: `6px 6px 0px ${inkBlack}`,
                padding: "16px 12px",
                textAlign: "center",
              }}
            >
              <div style={{ fontFamily: "'TekoDisplay', sans-serif", fontSize: 62, fontWeight: 700, lineHeight: 0.85 }}>
                0 KLIK
              </div>
              <div style={{ fontSize: 13, fontWeight: 900, letterSpacing: "0.08em", marginTop: 4 }}>
                FULL OTONOM
              </div>
            </div>
          </div>

          {/* Production Assets Vault */}
          <div
            style={{
              backgroundColor: pureWhite,
              border: `4px solid ${inkBlack}`,
              boxShadow: `8px 8px 0px ${inkBlack}`,
              padding: "20px 22px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: `2px solid ${inkBlack}`,
                paddingBottom: 8,
              }}
            >
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 900 }}>
                BUKTI EKSPERIMEN AKTIF DI SERVER
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: mintGreen }} />
                <span style={{ fontSize: 13, fontWeight: 900, color: inkBlack }}>SIAP POSTING</span>
              </div>
            </div>

            {[
              { id: "01", name: "Mesin Video Berbasis Kode", badge: "60 FPS MP4", color: mintGreen },
              { id: "02", name: "Animasi Otomatis Agen AI", badge: "60 FPS MP4", color: mintGreen },
              { id: "03", name: "Sinkronisasi Audio Otomatis", badge: "SEDANG RENDER", color: canaryYellow },
            ].map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: canvasBg,
                  border: `2px solid ${inkBlack}`,
                  padding: "10px 14px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 900, color: cobaltBlue }}>
                    {item.id}
                  </span>
                  <span style={{ fontSize: 17, fontWeight: 800 }}>{item.name}</span>
                </div>
                <span
                  style={{
                    backgroundColor: item.color,
                    color: inkBlack,
                    border: `1.5px solid ${inkBlack}`,
                    padding: "2px 8px",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 12,
                    fontWeight: 900,
                  }}
                >
                  {item.badge}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* =========================================================================
          SCENE 7: NEO-BRUTALIST LOGO OUTRO (Frames 1485 - 1710)
          VO: "Kalau lo mau tau sejauh mana AI masa depan bisa ngerubah cara kita bikin konten, follow Zura Tech Lab dan pantau eksperimen berikutnya."
          ========================================================================= */}
      {frame >= 1485 && (
        <div
          style={{
            position: "absolute",
            top: 330,
            left: 80,
            right: 80,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 20,
            zIndex: 30,
            transform: `scale(${interpolate(sp(1485), [0, 1], [0.88, 1])})`,
          }}
        >
          {/* Logo Badge in Pure White with Rigid 4px Black Border & 10px Hard Shadow */}
          <div
            style={{
              width: 220,
              height: 220,
              borderRadius: "50%",
              backgroundColor: pureWhite,
              border: `4px solid ${inkBlack}`,
              boxShadow: `10px 10px 0px ${inkBlack}`,
              padding: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            {/* Tilted Follow Badge */}
            <div
              style={{
                position: "absolute",
                top: -14,
                right: -10,
                backgroundColor: canaryYellow,
                color: inkBlack,
                border: `3px solid ${inkBlack}`,
                boxShadow: `4px 4px 0px ${inkBlack}`,
                padding: "4px 14px",
                fontFamily: "'TekoDisplay', sans-serif",
                fontSize: 22,
                fontWeight: 700,
                transform: "rotate(6deg)",
              }}
            >
              @zura.ailab
            </div>

            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                overflow: "hidden",
                backgroundColor: inkBlack,
              }}
            >
              <Img
                src={staticFile("zura_debut/zura_logo.jpeg")}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
          </div>

          <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 6 }}>
            <h2
              style={{
                fontFamily: "'TekoDisplay', sans-serif",
                fontSize: 98,
                fontWeight: 700,
                letterSpacing: "0.04em",
                lineHeight: 0.9,
                margin: 0,
                color: inkBlack,
              }}
            >
              ZURA TECH LAB
            </h2>
            <div
              style={{
                backgroundColor: inkBlack,
                color: canaryYellow,
                border: `3px solid ${inkBlack}`,
                padding: "6px 20px",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 22,
                fontWeight: 800,
                alignSelf: "center",
                boxShadow: `4px 4px 0px ${hazardOrange}`,
              }}
            >
              @zura.ailab
            </div>
            <p
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#333",
                maxWidth: 680,
                lineHeight: 1.4,
                margin: "8px 0 0 0",
              }}
            >
              Eksperimen AI Agent, Code-to-Video, dan teknologi masa depan.
            </p>
          </div>

          {/* Heavy Neo-Brutalist CTA Button in Hazard Orange */}
          <div
            style={{
              marginTop: 10,
              backgroundColor: hazardOrange,
              color: pureWhite,
              fontFamily: "'ChakraDisplay', sans-serif",
              fontWeight: 800,
              fontSize: 34,
              padding: "20px 60px",
              border: `4px solid ${inkBlack}`,
              boxShadow: `8px 8px 0px ${inkBlack}`,
              letterSpacing: "0.06em",
              display: "flex",
              alignItems: "center",
              gap: 14,
              cursor: "pointer",
            }}
          >
            <span>FOLLOW SEKARANG</span>
          </div>
        </div>
      )}

      {/* Neo-Brutalist Subtitle Component (Strict Safe Zone Y: 1520px / bottom: 400px) */}
      {(() => {
        const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);
        if (!currentSub) return null;
        const subSpring = spring({
          frame: frame - currentSub.from,
          fps,
          config: { damping: 14, stiffness: 180 },
        });

        return (
          <div
            style={{
              position: "absolute",
              bottom: 400,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              zIndex: 100,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                backgroundColor: pureWhite,
                border: `3.5px solid ${inkBlack}`,
                boxShadow: `6px 6px 0px ${inkBlack}`,
                padding: "16px 32px",
                maxWidth: 860,
                textAlign: "center",
                transform: `scale(${subSpring})`,
              }}
            >
              <span
                style={{
                  fontFamily: "'Space Grotesk', 'Inter', sans-serif",
                  fontSize: 32,
                  fontWeight: 800,
                  color: inkBlack,
                  lineHeight: 1.35,
                }}
              >
                {currentSub.highlight && currentSub.text.includes(currentSub.highlight) ? (
                  (() => {
                    const parts = currentSub.text.split(currentSub.highlight);
                    return (
                      <>
                        {parts[0]}
                        <span
                          style={{
                            backgroundColor: canaryYellow,
                            color: inkBlack,
                            border: `2px solid ${inkBlack}`,
                            boxShadow: `3px 3px 0px ${inkBlack}`,
                            padding: "2px 10px",
                            margin: "0 4px",
                            display: "inline-block",
                            transform: "rotate(-1deg)",
                          }}
                        >
                          {currentSub.highlight}
                        </span>
                        {parts.slice(1).join(currentSub.highlight)}
                      </>
                    );
                  })()
                ) : (
                  currentSub.text
                )}
              </span>
            </div>
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};